import React from "react";
import { useDispatch } from "react-redux";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import { logger } from "../../../../../../../utilities/logger";
import { dropZoneStyleSets } from "../../../../../config/controlStyles";
import { addUploadedFiles } from "../../../../../redux/modules/file";
import { setContentManagerMoveToSelectedFiles } from "../../../../../redux/modules/contentManagerDirectory";

interface IDraggableContainerProps {
    isValidDropZone: boolean;
    onDropBeforeFiles?: () => void;
    onDropAfterFiles?: (selectedGridItems?: any[]) => void;
    onDragHover?: () => void;
    isGrid?: boolean;
    gridItem?: any;
}

export const DraggableContainer: React.FunctionComponent<IDraggableContainerProps> = ({ isValidDropZone, children, onDropBeforeFiles, onDropAfterFiles, onDragHover, isGrid, gridItem }) => {

    const dispatch = useDispatch();

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    React.useEffect(() => {
        let debounceTimer;
        if (isValidDropZone && isDraggedOver && onDragHover) {
            debounceTimer = setTimeout(() => {
                onDragHover();
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [isValidDropZone, isDraggedOver, onDragHover]);

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event, isGrid, gridItem)) {
            event.preventDefault();
            event.stopPropagation();
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, [isGrid, gridItem]);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const handleDrop = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (DragAndDropHelper.validateDroppedFiles(event)) {
            try {
                if (onDropBeforeFiles) onDropBeforeFiles();
                dispatch(addUploadedFiles(await DragAndDropHelper.handleDroppedFiles(event)));
            } catch (error) {
                logger(error, "DraggableContainer.tsx (handleDrop)");
            }

            dragCounter.current = 0;
            if (onDropAfterFiles) onDropAfterFiles();
            setIsDraggedOver(false);
        }
        else if (DragAndDropHelper.validateDroppedGridItems(event, isGrid, gridItem)) {
            const draggedItems = JSON.parse(event.dataTransfer.getData(DragAndDropHelper.gridDataTransferType));
            dispatch(setContentManagerMoveToSelectedFiles(draggedItems));

            dragCounter.current = 0;
            if (onDropAfterFiles) onDropAfterFiles(draggedItems);
            setIsDraggedOver(false);
        }
    }, [isGrid, gridItem, onDropBeforeFiles, onDropAfterFiles]);

    return (
        <>
            {isValidDropZone
                ? <div
                    className={isDraggedOver ? `dropzone ${dropZoneStyleSets.dropzoneActive}` : "dropzone"}
                    style={{ height: "100%" }}
                    onDragEnter={dragEnter}
                    onDragOver={dragOver}
                    onDragLeave={dragLeave}
                    onDrop={handleDrop}
                >
                    {children}
                </div>
                : <>{children}</>}
        </>
    )
};